import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { usePartnerPlansPaymentsQuery } from '../../../api/use-partner-plans-payments-query';
import PortalPaginationControls from '../../layout/PortalPaginationControls';
import { VISIBLE_PAGE_COUNT } from '../../../constants';
import { getPortalCardSx } from '../../../styles/portalSurfaces';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../../styles/portalListTable';
import { usePortalTokens } from '../../../styles/usePortalTokens';
import { PARTNER_PLAN_PAYMENTS_PAGE_SIZE } from '../../../utils/partnerSubscriptionPayments';

const PartnerSubscriptionPayments = ({ onBack }) => {
  const portal = usePortalTokens();
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError, error } = usePartnerPlansPaymentsQuery({
    page,
    limit: PARTNER_PLAN_PAYMENTS_PAGE_SIZE,
  });

  const payments = data?.payments ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Number(data?.totalPages ?? 1));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2.5,
          px: { xs: 0.5, sm: 0 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {onBack ? (
            <Button
              onClick={onBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: portal.accent,
                px: 0,
                mb: 1,
                minWidth: 0,
                alignSelf: 'flex-start',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Back to subscription
            </Button>
          ) : null}
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.375rem' },
              color: 'text.primary',
              letterSpacing: '-0.01em',
              mb: 0.5,
              lineHeight: 1.3,
            }}
          >
            Payment history
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Successful portal fee payments for your organization, newest first.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...getPortalCardSx(portal, { borderRadius: '16px', overflow: 'hidden' }),
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress color="primary" size={28} />
          </Box>
        ) : null}

        {isError ? (
          <Typography color="error" variant="body2" textAlign="center" sx={{ py: 4, px: 2 }}>
            {error?.message || 'Failed to load payment history'}
          </Typography>
        ) : null}

        {!isLoading && !isError && payments.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ py: 5, px: 2 }}
          >
            No payments yet.
          </Typography>
        ) : null}

        {!isLoading && !isError && payments.length > 0 ? (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <PortalTableHead>
                  <PortalTableRow>
                    <PortalTableCell>Paid at</PortalTableCell>
                    <PortalTableCell>Amount</PortalTableCell>
                    <PortalTableCell>Billing period</PortalTableCell>
                    <PortalTableCell>Plan</PortalTableCell>
                    <PortalTableCell>Invoice</PortalTableCell>
                    <PortalTableCell>Status</PortalTableCell>
                  </PortalTableRow>
                </PortalTableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <PortalTableRow key={payment.paymentId || payment.invoiceNumber}>
                      <PortalTableCell>{payment.paidAtLabel || '—'}</PortalTableCell>
                      <PortalTableCell sx={{ fontWeight: 700 }}>
                        {payment.amountLabel}
                      </PortalTableCell>
                      <PortalTableCell>{payment.billingPeriodLabel || '—'}</PortalTableCell>
                      <PortalTableCell>{payment.planName || '—'}</PortalTableCell>
                      <PortalTableCell>
                        {payment.invoiceUrl ? (
                          <Link
                            href={payment.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontWeight: 600,
                              color: portal.accent,
                              maxWidth: 220,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 190,
                              }}
                            >
                              {payment.invoiceNumber || 'View invoice'}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                          </Link>
                        ) : (
                          payment.invoiceNumber || '—'
                        )}
                      </PortalTableCell>
                      <PortalTableCell>
                        <Chip
                          size="small"
                          label={payment.status || 'success'}
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 700,
                            height: 24,
                            bgcolor: 'success.light',
                            color: 'success.dark',
                          }}
                        />
                      </PortalTableCell>
                    </PortalTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderTopColor: portal.border,
                }}
              >
                <PortalPaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  visibleCount={VISIBLE_PAGE_COUNT}
                  isLoading={isFetching}
                  onPageChange={setPage}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default PartnerSubscriptionPayments;
